import { useStore } from './StoreProvider';
import {
  recordTournamentResult,
  resultsAreServerAuthoritative,
  ResultRefused,
} from '../lib/api/tournamentResults';

// ─────────────────────────────────────────────────────────────────────
// useServerResultRecording — the portal's result writer, server-owned.
//
// The browser used to apply the result itself and write the club's whole
// session document back. That is one rule with two implementations (the pad
// has had a server path since #183), and the browser's had no exactly-once
// boundary at all: a double-tap, a retry after a dropped answer or a second
// tab each applied the ELO again.
//
// Since #404 the server owns it — same engine appliers, plus a `result_commits`
// claim and the players' global rating commit, in one transaction. This hook is
// how the portal reaches it.
//
// IT IS NOT OPTIMISTIC, and that is the behaviour change. The old path put the
// result on screen first and wrote afterwards, so a refused or lost write left
// a match looking played that the database says was not — and the next reload
// undid it in front of whoever entered it. Here the screen changes only after
// the server says it applied.
//
// `resultsAreServerAuthoritative()` gates it: with the in-memory backend there
// is no server to own anything, so the caller keeps its existing local path.
// Same shape as the court-state command (#271).
// ─────────────────────────────────────────────────────────────────────

export interface ServerResultInput {
  /** The tournament's own id. The server decides where that id lives. */
  sessionId: string;
  matchId: string;
  score1?: number | null;
  score2?: number | null;
  score?: unknown;
}

export interface ServerResultOutcome {
  /** The session AFTER the result — already in state by the time this returns. */
  session: any;
  /**
   * The result was already recorded, and this response RECOVERED it.
   *
   * Not merely «you need not try again». A request can commit and lose its
   * response, so the browser retrying is still showing the match as unplayed
   * and holding a version from before a write it never heard about. The server
   * answers a replay with the stored session and the row's current version,
   * and both are adopted here exactly as a fresh application's are.
   */
  replayed: boolean;
}

export function useServerResultRecording() {
  const store = useStore();

  /**
   * Record one result and WAIT for the server to have applied it.
   *
   * Resolves once the new session is in state; rejects with `ResultRefused`
   * when the server refuses (already recorded, not running, unknown player),
   * which the caller shows to the user.
   */
  const recordServerResult = async (input: ServerResultInput): Promise<ServerResultOutcome> => {
    const res = await recordTournamentResult(input);

    // NOTHING TO ADOPT only when the server sent nothing to adopt. A replay
    // now carries the stored session, and that is the whole point — it is how
    // a browser whose first attempt lost its response catches up without a
    // reload. What must never happen is clearing state from an ABSENT session,
    // which would blank a tournament on a double-tap.
    if (res.session === undefined || res.session === null) {
      return { session: null, replayed: res.replayed };
    }

    const active = store.tournament as any;
    if (active && (active.id === input.sessionId || active.displayId === input.sessionId)) {
      // The row's value IS this session, so the version can be adopted.
      store.adoptServerTournament(res.session, res.storage);
      return { session: res.session, replayed: res.replayed };
    }

    // A PAUSED tournament. The row holds the whole list and the response holds
    // one session, so the list is rebuilt for the screen — but the version is
    // NOT adopted for a document this client did not receive. `null` says so,
    // and the key is marked for a refresh before the next write.
    const nextList = (store.pausedTournaments as any[]).map((t) =>
      (t?.id ?? t?.displayId) === input.sessionId ? res.session : t,
    );
    store.adoptServerPausedTournaments(
      nextList,
      res.storage ? { key: res.storage.key, version: res.storage.version } : undefined,
      /* holdsDocument */ false,
    );
    return { session: res.session, replayed: res.replayed };
  };

  return { recordServerResult, resultsAreServerAuthoritative, ResultRefused };
}
